/**
 * Advanced Metrics Panel Component (Meeting 2025-10-03)
 * Displays FFT Spectrum, Peak Energy, and Charging/Discharging analyses
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  useTheme,
  Skeleton
} from '@mui/material';
import {
  ShowChart,
  BarChart,
  BatteryChargingFull,
  Functions
} from '@mui/icons-material';
import apiClient from '../services/api';
import PowerProfileChart from './PowerProfileChart';
import ZoomableCanvas from './ZoomableCanvas';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`advanced-metrics-tabpanel-${index}`}
      aria-labelledby={`advanced-metrics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `advanced-metrics-tab-${index}`,
    'aria-controls': `advanced-metrics-tabpanel-${index}`,
  };
}

interface AdvancedMetricsPanelProps {
  uploadId: string;
}

const AdvancedMetricsPanel: React.FC<AdvancedMetricsPanelProps> = ({ uploadId }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // State for each analysis type
  const [fftData, setFftData] = useState<any>(null);
  const [peakEnergyData, setPeakEnergyData] = useState<any>(null);
  const [chargingDischargingData, setChargingDischargingData] = useState<any>(null);
  const [histogramData, setHistogramData] = useState<any>(null);

  // Helper function to format power values with correct unit
  const formatPower = (valueInWatts: number, powerUnit: string): string => {
    if (powerUnit === 'kilowatt') {
      return `${(valueInWatts / 1000).toFixed(2)} kW`;
    }
    return `${valueInWatts.toFixed(0)} W`;
  };

  // Helper function to format energy values with correct unit
  const formatEnergy = (valueInWh: number, powerUnit: string): string => {
    if (powerUnit === 'kilowatt') {
      return `${(valueInWh / 1000).toFixed(2)} kWh`;
    }
    return `${valueInWh.toFixed(2)} Wh`;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (!uploadId) return;

    const loadAdvancedMetrics = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load all advanced metrics in parallel
        const [fft, peakEnergy, chargingDischarging, histogram] = await Promise.all([
          apiClient.getFFTSpectrum(uploadId),
          apiClient.getPeakEnergy(uploadId),
          apiClient.getChargingDischarging(uploadId),
          apiClient.getPowerHistogram(uploadId)
        ]);

        setFftData(fft);
        setPeakEnergyData(peakEnergy);
        setChargingDischargingData(chargingDischarging);
        setHistogramData(histogram);
      } catch (err: any) {
        console.error('Failed to load advanced metrics:', err);
        setError(err.message || 'Fehler beim Laden der erweiterten Metriken');
      } finally {
        setLoading(false);
      }
    };

    loadAdvancedMetrics();
  }, [uploadId]);

  if (loading) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Erweiterte Metriken werden geladen...
          </Typography>
          <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>Fehler beim Laden</Typography>
        <Typography variant="body1">{error}</Typography>
      </Alert>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="advanced metrics tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<Functions />}
            label="FFT Spektrum"
            {...a11yProps(0)}
            iconPosition="start"
          />
          <Tab
            icon={<ShowChart />}
            label="Peak Energy"
            {...a11yProps(1)}
            iconPosition="start"
          />
          <Tab
            icon={<BatteryChargingFull />}
            label="Laden/Entladen"
            {...a11yProps(2)}
            iconPosition="start"
          />
          <Tab
            icon={<BarChart />}
            label="Histogramm"
            {...a11yProps(3)}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* FFT Spectrum Tab */}
      <TabPanel value={tabValue} index={0}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          FFT Frequenzspektrum
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Frequenzanalyse des Leistungsprofils zur Identifikation dominanter Frequenzen
        </Typography>

        {fftData?.chart?.data && (
          <Box sx={{ minHeight: 450 }}>
            <ZoomableCanvas title="FFT Frequenzspektrum">
              <PowerProfileChart
                chartData={fftData.chart.data}
                title=""
                height={450}
              />
            </ZoomableCanvas>
          </Box>
        )}

        {fftData?.data && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Analyse-Ergebnisse:
            </Typography>
            <Typography variant="body2">
              • Dominante Frequenz: <strong>{fftData.data.dominant_frequency_hz?.toFixed(4)} Hz</strong>
            </Typography>
            <Typography variant="body2">
              • Maximale Magnitude: <strong>{fftData.data.max_magnitude?.toFixed(2)}</strong>
            </Typography>
            <Typography variant="body2">
              • Frequenz-Punkte: <strong>{fftData.data.frequencies?.length || 0}</strong>
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Peak Energy Tab */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Peak Energy Analyse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Analyse der Energieanteile von Spitzenlast-Events im Lastprofil
        </Typography>

        {peakEnergyData?.chart?.data && (
          <Box sx={{ minHeight: 450 }}>
            <ZoomableCanvas title="Peak Energy Analyse">
              <PowerProfileChart
                chartData={peakEnergyData.chart.data}
                title=""
                height={450}
              />
            </ZoomableCanvas>
          </Box>
        )}

        {peakEnergyData?.data && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Analyse-Ergebnisse:
            </Typography>
            <Typography variant="body2">
              • Peak Energy Share: <strong>{peakEnergyData.data.peak_energy_share_percent?.toFixed(2)}%</strong>
            </Typography>
            <Typography variant="body2">
              • Peak Events: <strong>{peakEnergyData.data.peak_event_count || 0}</strong>
            </Typography>
            <Typography variant="body2">
              • Durchschnittliche Peak-Dauer: <strong>{peakEnergyData.data.peak_duration_avg_s?.toFixed(1)}s</strong>
            </Typography>
            <Typography variant="body2">
              • Peak-Schwellenwert: <strong>{formatPower(peakEnergyData.data.peak_threshold_w || 0, peakEnergyData.metadata?.power_unit || 'watt')}</strong>
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Charging/Discharging Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Lade- und Entladeenergie
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Trennung von Lade- und Entladevorgängen zur Effizienzberechnung
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Hinweis:</strong> Diese Analyse erkennt Lade-/Entladevorgänge anhand von positiven und negativen Leistungswerten.
            Normale Lastprofile enthalten nur positive Werte (Verbrauch), daher werden hier 0 Wh angezeigt.
            Für Batteriesysteme mit Rekuperation (z.B. Elektrofahrzeuge) würden negative Werte beim Bremsen erfasst.
          </Typography>
        </Alert>

        {chargingDischargingData?.chart?.data && (
          <Box sx={{ minHeight: 450 }}>
            <PowerProfileChart
              chartData={chargingDischargingData.chart.data}
              title=""
              height={450}
            />
          </Box>
        )}

        {chargingDischargingData?.data && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Analyse-Ergebnisse:
            </Typography>
            <Typography variant="body2">
              • Ladeenergie: <strong>{formatEnergy(chargingDischargingData.data.charging_energy_wh || 0, chargingDischargingData.metadata?.power_unit || 'watt')}</strong>
            </Typography>
            <Typography variant="body2">
              • Entladeenergie: <strong>{formatEnergy(chargingDischargingData.data.discharging_energy_wh || 0, chargingDischargingData.metadata?.power_unit || 'watt')}</strong>
            </Typography>
            <Typography variant="body2">
              • Effizienz (Round-Trip): <strong>{chargingDischargingData.data.round_trip_efficiency_percent?.toFixed(1)}%</strong>
            </Typography>
            <Typography variant="body2">
              • Ladevorgänge: <strong>{chargingDischargingData.data.charging_event_count || 0}</strong>
            </Typography>
            <Typography variant="body2">
              • Entladevorgänge: <strong>{chargingDischargingData.data.discharging_event_count || 0}</strong>
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Histogram Tab */}
      <TabPanel value={tabValue} index={3}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Leistungsverteilung (Histogramm)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Statistische Verteilung der Leistungswerte mit Quartilanalyse
        </Typography>

        {histogramData?.chart?.data && (
          <Box sx={{ minHeight: 450 }}>
            <PowerProfileChart
              chartData={histogramData.chart.data}
              title=""
              height={450}
            />
          </Box>
        )}

        {histogramData?.data && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Analyse-Ergebnisse:
            </Typography>
            <Typography variant="body2">
              • Peak Bin: <strong>{formatPower(histogramData.data.peak_bin_power_w || 0, histogramData.metadata?.power_unit || 'watt')}</strong>
              {' '}({histogramData.data.peak_bin_count} Datenpunkte)
            </Typography>
            <Typography variant="body2">
              • Bins: <strong>{histogramData.data.bins || 0}</strong>
            </Typography>
            <Typography variant="body2">
              • Min. Leistung: <strong>{formatPower(histogramData.data.min_power_w || 0, histogramData.metadata?.power_unit || 'watt')}</strong>
            </Typography>
            <Typography variant="body2">
              • Max. Leistung: <strong>{formatPower(histogramData.data.max_power_w || 0, histogramData.metadata?.power_unit || 'watt')}</strong>
            </Typography>
          </Box>
        )}
      </TabPanel>
    </Paper>
  );
};

export default AdvancedMetricsPanel;
